import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import { useCancelSopGeneration, useDeleteSop, useRegenerateSop, useSop, useUpdateSop } from '../hooks.ts';
import { SopStatusBadge } from '../components/SopStatusBadge.tsx';
import GenerationProgress from '../components/GenerationProgress.tsx';
import SopSectionBody from '../components/SopSectionBody.tsx';
import CitationPanel from '../components/CitationPanel.tsx';
import { formatDuration, formatWhen } from '../format.ts';
import { isSopEditable, LOCKED_SOP_MESSAGE, type Sop } from '../../../request/sop-synthesizer/sopSynthesizer.api.ts';

function EditDetailsForm({ sop, onDone }: { sop: Sop; onDone: () => void }) {
  const update = useUpdateSop(sop.id);
  const [title, setTitle] = useState(sop.title);
  const [description, setDescription] = useState(sop.description ?? '');
  const invalid = !title.trim();
  return (
    <form
      className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!invalid) update.mutate({ title: title.trim(), description: description.trim() }, { onSuccess: onDone });
      }}
    >
      <div>
        <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700">Title</label>
        <input id="edit-title" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={title} maxLength={255} onChange={(e) => setTitle(e.target.value)} />
        {invalid && <p className="mt-1 text-xs text-red-600">Enter a title</p>}
      </div>
      <div>
        <label htmlFor="edit-desc" className="block text-sm font-medium text-gray-700">Description</label>
        <textarea id="edit-desc" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" rows={3} value={description} maxLength={4000} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
        <button type="submit" disabled={invalid || update.isPending} className="rounded-md bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50">
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export default function SopDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useSop(id);
  const regenerate = useRegenerateSop(id ?? '');
  const cancel = useCancelSopGeneration(id ?? '');
  const remove = useDeleteSop();
  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [instructions, setInstructions] = useState('');

  const citations = useMemo(() => new Map((data?.citations ?? []).map((c) => [c.marker, c])), [data?.citations]);
  const known = useMemo(() => new Set(citations.keys()), [citations]);

  if (isLoading) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  if (error || !data) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <Link to="/sop-synthesizer" className="text-sm text-blue-700 hover:underline">← Back to SOPs</Link>
        <p className="mt-4 text-sm text-red-600">SOP not found.</p>
      </div>
    );
  }

  const { sop, sections, run, frames, video } = data;
  const generating = sop.status === 'generating';
  const editable = isSopEditable(sop.status);
  const empty = sections.length === 0;
  const failed = !generating && run?.status === 'failed';
  const canGenerate = editable && !generating;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <Link to="/sop-synthesizer" className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to SOPs
      </Link>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{sop.title}</h1>
          {sop.description && <p className="mt-1 max-w-2xl whitespace-pre-line text-sm text-gray-700">{sop.description}</p>}
          <p className="mt-1 text-sm text-gray-500">
            From video “{video.title ?? 'video'}”{video.duration_seconds ? ` (${formatDuration(video.duration_seconds)})` : ''} · updated {formatWhen(sop.updated_at)}
          </p>
          <div className="mt-2"><SopStatusBadge status={sop.status} run={run} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {editable && !editing && (
            <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <Pencil className="h-4 w-4" /> Edit details
            </button>
          )}
          {canGenerate && !empty && (
            <button
              type="button"
              onClick={() => regenerate.mutate(undefined)}
              disabled={regenerate.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Replaces the whole draft"
            >
              <RefreshCw className="h-4 w-4" /> Regenerate
            </button>
          )}
          {confirmDelete ? (
            <span className="inline-flex items-center gap-2 text-sm">
              <button
                type="button"
                className="rounded-md bg-red-600 px-3 py-2 text-white hover:bg-red-700 disabled:opacity-50"
                disabled={remove.isPending}
                onClick={() => remove.mutate(sop.id, { onSuccess: () => navigate('/sop-synthesizer') })}
              >
                Confirm delete
              </button>
              <button type="button" className="text-gray-600 hover:underline" onClick={() => setConfirmDelete(false)}>Cancel</button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => editable && setConfirmDelete(true)}
              aria-disabled={!editable}
              title={editable ? 'Delete this SOP' : LOCKED_SOP_MESSAGE}
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${editable ? 'border-red-300 text-red-700 hover:bg-red-50' : 'cursor-not-allowed border-gray-200 text-gray-400'}`}
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
        </div>
      </div>
      {!editable && <p className="mt-2 text-xs text-gray-500">{LOCKED_SOP_MESSAGE}</p>}

      <div className="mt-6 space-y-6">
        {editing && <EditDetailsForm sop={sop} onDone={() => setEditing(false)} />}

        {generating && <GenerationProgress run={run} onCancel={() => cancel.mutate()} cancelling={cancel.isPending} />}

        {failed && empty && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-800">The SOP could not be generated.</p>
            <p className="mt-1 text-sm text-red-700">{run?.error_message}</p>
          </div>
        )}

        {failed && !empty && (
          <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
            The last regeneration failed ({run?.error_message}). The previous draft below is unchanged.
          </p>
        )}

        {canGenerate && empty && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center">
            <h2 className="text-sm font-medium text-gray-900">{failed ? 'Try again' : 'No content yet'}</h2>
            <p className="mt-1 text-sm text-gray-500">
              The draft is written from the video in the background. It can take several minutes, and you can leave the page while it runs.
            </p>
            <label htmlFor="gen-guidance" className="sr-only">Guidance for the draft</label>
            <textarea
              id="gen-guidance"
              className="mx-auto mt-3 block w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-left text-sm"
              rows={2}
              maxLength={2000}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Optional guidance, e.g. focus on the month-end steps"
            />
            <button
              type="button"
              onClick={() => regenerate.mutate(instructions.trim() || undefined)}
              disabled={regenerate.isPending}
              className="mt-3 inline-flex items-center gap-2 rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" /> {failed ? 'Generate again' : 'Generate draft'}
            </button>
          </div>
        )}

        {!empty && (
          <div className="lg:grid lg:grid-cols-[14rem_1fr] lg:gap-8">
            <nav aria-label="Sections" className="mb-6 hidden lg:block">
              <ul className="sticky top-4 space-y-1 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#sec-${s.id}`} className="block rounded px-2 py-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="space-y-8">
              {sections.map((s) => (
                <section key={s.id} id={`sec-${s.id}`} className="rounded-lg border border-gray-200 bg-white p-5">
                  <h2 className="mb-3 text-lg font-semibold text-gray-900">{s.heading}</h2>
                  <SopSectionBody body={s.body} frames={frames} knownMarkers={known} onCite={setActiveMarker} />
                </section>
              ))}
              <p className="text-xs text-gray-400">
                Drafted by AI from an automatic transcript. Numbered chips link each statement to the moment in the video it came from; review before relying on it.
              </p>
            </div>
          </div>
        )}
      </div>

      <CitationPanel
        citation={activeMarker ? citations.get(activeMarker) ?? null : null}
        videoUrl={video.url}
        onClose={() => setActiveMarker(null)}
      />
    </div>
  );
}
